import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Pill,
  Search,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Heart,
  ShieldAlert,
  Activity,
  Brain,
  Droplet,
  Zap,
  Bug
} from 'lucide-react';

import { API_URL } from '../config';

export default function MedicamentsAdultePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (medicationId, medicationName, content) => {
    setAiLoading({ ...aiLoading, [medicationId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'medicaments_adulte',
          audience: 'adult',
          prompt: `Analyse pharmaceutique pour ${medicationName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Indications principales et mécanisme d'action
2. Posologie optimale et voies d'administration
3. Contre-indications absolues
4. Effets indésirables à surveiller
5. Interactions médicamenteuses majeures`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [medicationId]: data.response });
        toast.success(`Analyse AI ${medicationName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [medicationId]: false });
    }
  };

  const medications = [
    {
      id: 'acide-tranexamique',
      commercial: 'EXACYL®',
      dci: 'Acide tranexamique',
      indication: 'Hémorragies par fibrinolyse, traumatismes',
      preparation: 'Ampoules de 500 mg/5 mL',
      posologie: `**Adulte:**
• **Dose:** 1 g (2 ampoules de 500 mg)
• **Voie:** IV lente (10 min)
• **Puis:** Perfusion 1 g sur 8h
• **Maximum:** 3 g/24h

**Indications principales:**
• Choc hémorragique traumatique
• Hémorragie digestive
• Hémoptysie grave

**⚠️ Contre-indications:**
• Thrombose évolutive
• Antécédent thromboembolique < 1 an`
    },
    {
      id: 'adrenaline',
      commercial: 'ADRENALINE AGUETTANT®',
      dci: 'Adrénaline (Épinéphrine)',
      indication: 'Choc anaphylactique, ACR, bronchospasme sévère',
      preparation: 'Ampoules 1 mg/1 mL',
      posologie: `**Choc anaphylactique:**
• **Dose:** 0,01 mg/kg IM (max 0,5 mg)
• **Localisation:** Face antérolatérale de cuisse
• **Répétition:** Possible toutes les 5 min

**ACR (Adulte):**
• **Dose:** 1 mg IVD
• **Répétition:** Toutes les 3-5 min
• **Perfusion:** 0,05-0,5 µg/kg/min

**Bronchospasme:**
• **Aérosol:** 0,1 mg/kg (max 5 mg)

**⚠️ Précautions:**
• Surveillance FC, PA
• Risque ischémie myocardique
• Éviter voie sous-cutanée`
    },
    {
      id: 'amiodarone',
      commercial: 'CORDARONE®',
      dci: 'Amiodarone',
      indication: 'Troubles du rythme ventriculaire, FA rapide',
      preparation: 'Ampoules 150 mg/3 mL',
      posologie: `**ACR (TV/FV réfractaire):**
• **1ère dose:** 300 mg IVD rapide
• **2ème dose:** 150 mg si persistance

**Troubles du rythme avec pouls:**
• **Charge:** 300 mg IV en 10-60 min
• **Entretien:** 900 mg/24h IVSE
• **Dilution:** 250-500 mL G5%

**⚠️ Effets indésirables:**
• Hypotension (perfusion rapide)
• Bradycardie
• Phototoxicité cutanée
• Dysthyroïdie (traitement prolongé)

**Contre-indications:**
• Bradycardie sévère
• BAV de haut degré sans PM
• Hyperthyroïdie`
    },
    {
      id: 'atropine',
      commercial: 'ATROPINE SULFATE®',
      dci: 'Atropine sulfate',
      indication: 'Bradycardie symptomatique, intoxication',
      preparation: 'Ampoules 1 mg/1 mL',
      posologie: `**Bradycardie symptomatique:**
• **Dose:** 0,5-1 mg IVD
• **Répétition:** Toutes les 3-5 min
• **Maximum:** 3 mg

**Intoxication organophosphorés:**
• **Dose initiale:** 2 mg IVD
• **Répétition:** Doublement des doses si nécessaire
• **Objectif:** Atropinisation (mydriase, FC > 80)

**⚠️ Effets indésirables:**
• Tachycardie
• Mydriase
• Rétention urinaire
• Confusion (sujet âgé)

**Contre-indications:**
• Glaucome angle fermé
• Adénome prostatique symptomatique`
    },
    {
      id: 'calcium',
      commercial: 'CHLORURE DE CALCIUM 10%',
      dci: 'Chlorure de calcium',
      indication: 'Hypocalcémie, hyperkaliémie, intoxication',
      preparation: 'Ampoules 10 mL = 1 g',
      posologie: `**Choc hémorragique avec transfusion massive:**
• **Dose:** 1 g (1 ampoule)
• **Voie:** IV lente séparée
• **Répétition:** Selon calcémie ionisée
• **Objectif:** Ca²⁺ ≥ 0,9 mmol/L

**Hyperkaliémie sévère:**
• **Dose:** 10 mL (1 g) sur 2-5 min
• **Monitoring:** ECG continu

**Intoxication bloqueurs calciques:**
• **Bolus:** 10-20 mL sur 5-10 min
• **Perfusion:** 0,2-0,5 mL/kg/h

**⚠️ Précautions:**
• Voie SEPARÉE (précipitation transfusion)
• Extravasation = nécrose tissulaire
• Bradycardie possible`
    },
    {
      id: 'ceftriaxone',
      commercial: 'ROCEPHINE®',
      dci: 'Ceftriaxone',
      indication: 'Infections bactériennes sévères',
      preparation: 'Flacons 1 g, 2 g',
      posologie: `**Méningite purulente:**
• **Dose:** 2 g IVD
• **Puis:** 2 g x 2/jour

**Pneumonie communautaire grave:**
• **Dose:** 1-2 g x 1/jour IV

**Infections intra-abdominales:**
• **Dose:** 2 g x 1/jour
• **Association:** Métronidazole

**Pédiatrie:**
• **Dose:** 50 mg/kg/j (max 2 g)

**⚠️ Contre-indications:**
• Allergie béta-lactamines
• Prématuré < 41 SA (incompatibilité calcium)
• Ictère néonatal

**Effets indésirables:**
• Diarrhée
• Réactions allergiques
• Lithiase biliaire`
    },
    {
      id: 'diazepam',
      commercial: 'VALIUM®',
      dci: 'Diazépam',
      indication: 'État de mal épileptique, anxiolyse',
      preparation: 'Ampoules 10 mg/2 mL',
      posologie: `**État de mal épileptique:**
• **1ère dose:** 10 mg IVL (1 mg/min)
• **Répétition:** 10 mg après 5 min si persistance
• **Intrarectal:** 0,5 mg/kg si pas de VVP

**Anxiolyse procédurale:**
• **Dose:** 5-10 mg IV titration
• **Début action:** 2-5 min
• **Durée:** 15-60 min

**Sevrage alcoolique:**
• **Dose:** 10-20 mg/6-8h

**⚠️ Effets indésirables:**
• Dépression respiratoire
• Hypotension
• Confusion (sujet âgé)
• Risque dépendance

**Antidote:**
• Flumazénil (Anexate®) 0,25 mg IVL`
    },
    {
      id: 'dobutamine',
      commercial: 'DOBUTREX®',
      dci: 'Dobutamine',
      indication: 'Insuffisance cardiaque aiguë, choc cardiogénique',
      preparation: 'Ampoules 250 mg/20 mL',
      posologie: `**Choc cardiogénique:**
• **Dose initiale:** 2-5 µg/kg/min
• **Titration:** ↑ 2-5 µg/kg/min/15 min
• **Dose usuelle:** 5-10 µg/kg/min
• **Maximum:** 20 µg/kg/min

**Préparation:**
• **250 mg dans 250 mL:** 1 mg/mL
• **Débit (mL/h) = Dose (µg/kg/min) × Poids (kg) × 0,06**

**Indications:**
• Bas débit cardiaque
• OAP cardiogénique
• Choc hémorragique (après remplissage)

**⚠️ Effets indésirables:**
• Tachycardie
• Arythmies ventriculaires
• Ischémie myocardique
• Hypotension (vasodilatation)

**Surveillance:**
• PA, FC, ECG continu
• ScvO2 si possible`
    },
    {
      id: 'fentanyl',
      commercial: 'FENTANYL®',
      dci: 'Fentanyl',
      indication: 'Analgésie, sédation-analgésie',
      preparation: 'Ampoules 0,05 mg/mL (100 µg/2 mL)',
      posologie: `**Analgésie procédurale:**
• **Bolus:** 1-2 µg/kg IVL (25-100 µg)
• **Répétition:** Toutes les 3-5 min
• **Délai action:** 2-3 min
• **Durée:** 30-60 min

**Sédation-analgésie (intubation):**
• **Co-induction:** 3-5 µg/kg
• **Entretien IVSE:** 1-5 µg/kg/h

**Douleur intense (trauma):**
• **Titration:** 25-50 µg/5 min
• **Maximum:** 200 µg initial

**⚠️ Effets indésirables:**
• Dépression respiratoire
• Rigidité thoracique (doses élevées)
• Bradycardie
• Nausées/vomissements

**Antidote:**
• Naloxone (Narcan®) 0,04-0,4 mg IVL`
    },
    {
      id: 'furosemide',
      commercial: 'LASILIX®',
      dci: 'Furosémide',
      indication: 'OAP, surcharge hydrosodée',
      preparation: 'Ampoules 20 mg/2 mL, 250 mg/25 mL',
      posologie: `**OAP cardiogénique:**
• **Dose:** 40-80 mg IVD
• **Répétition:** Doublement si inefficace
• **Perfusion:** 5-10 mg/h si résistance

**Surcharge hydrosodée:**
• **Dose:** 20-40 mg IVD
• **Adaptation:** Selon diurèse

**Insuffisance rénale:**
• **Dose:** 80-240 mg
• **Perfusion continue:** Plus efficace

**⚠️ Effets indésirables:**
• Hypokaliémie
• Hyponatrémie
• Hypovolémie
• Ototoxicité (doses massives)

**Surveillance:**
• Kaliémie
• Diurèse
• État d'hydratation`
    },
    {
      id: 'heparine',
      commercial: 'HÉPARINE SODIQUE®',
      dci: 'Héparine non fractionnée',
      indication: 'Syndrome coronarien aigu, EP, prévention thrombotique',
      preparation: 'Flacons 5000 UI/mL',
      posologie: `**SCA ST+ (avant fibrinolyse):**
• **Bolus:** 60 UI/kg (max 4000 UI)
• **Perfusion:** 12 UI/kg/h (max 1000 UI/h)

**SCA sans ST+:**
• **Bolus:** 60-70 UI/kg (max 5000 UI)
• **Perfusion:** 12-15 UI/kg/h

**Embolie pulmonaire:**
• **Bolus:** 80 UI/kg
• **Perfusion:** 18 UI/kg/h
• **Objectif TCA:** 1,5-2,5 fois témoin

**Prévention thrombose:**
• **Dose:** 5000 UI SC x 2-3/j

**⚠️ Surveillance:**
• TCA (4-6h après bolus, puis quotidien)
• Plaquettes (J0, J3, J5)
• Risque TIH (thrombopénie induite)

**Antidote:**
• Protamine 1 mg neutralise 100 UI`
    },
    {
      id: 'hydrocortisone',
      commercial: 'HYDROCORTISONE HEMISUCCINATE®',
      dci: 'Hydrocortisone',
      indication: 'Choc anaphylactique, insuffisance surrénale, asthme',
      preparation: 'Flacons 100 mg',
      posologie: `**Choc anaphylactique:**
• **Dose:** 200 mg (2 flacons) IVL
• **Répétition:** Toutes les 4-6h si besoin

**Insuffisance surrénale aiguë:**
• **Dose:** 100 mg IVD
• **Puis:** 200 mg/24h IVSE

**Asthme aigu grave:**
• **Dose:** 100-200 mg IV
• **Puis:** 100 mg/6h

**Laryngite/œdème laryngé:**
• **Dose:** 4 mg/kg (max 200 mg)

**⚠️ Effets indésirables:**
• Hyperglycémie
• HTA
• Rétention hydrosodée
• Immunosuppression

**Équivalence:**
• 100 mg hydrocortisone = 25 mg prednisolone`
    },
    {
      id: 'ketamine',
      commercial: 'KÉTAMINE®',
      dci: 'Kétamine',
      indication: 'Analgésie, sédation, induction',
      preparation: 'Ampoules 50 mg/mL (500 mg/10 mL)',
      posologie: `**Analgésie sub-dissociative:**
• **Dose:** 0,2-0,5 mg/kg IV lente
• **Répétition:** Toutes les 10-15 min
• **Maximum:** 1 mg/kg/h

**Induction (ISR):**
• **Dose:** 1-2 mg/kg IVD
• **Début action:** 30-60 sec
• **Durée:** 10-15 min

**Sédation-analgésie (entretien):**
• **Perfusion:** 1-3 mg/kg/h

**Asthme aigu grave:**
• **Perfusion:** 0,5-2 mg/kg/h

**⚠️ Effets indésirables:**
• Hallucinations (↓ par BZD)
• Hypersialorrhée (atropine préventive)
• Tachycardie, HTA
• ↑ PIC (relatif)
• Laryngospasme (rare)

**Avantages:**
• Maintien drive respiratoire
• Bronchodilatation
• Stabilité hémodynamique`
    },
    {
      id: 'labetalol',
      commercial: 'TRANDATE®',
      dci: 'Labétalol',
      indication: 'Urgence hypertensive, dissection aortique',
      preparation: 'Ampoules 100 mg/20 mL',
      posologie: `**Crise hypertensive:**
• **Bolus initial:** 10-20 mg IVL (2 min)
• **Répétition:** 20-80 mg/10 min
• **Maximum cumulé:** 300 mg

**Perfusion continue:**
• **Dose:** 0,5-2 mg/min
• **Préparation:** 200 mg dans 200 mL

**Dissection aortique:**
• **Objectif:** PAS 100-120 mmHg
• **Association:** Analgésie morphinique

**⚠️ Contre-indications:**
• Asthme, BPCO sévère
• BAV 2-3, bradycardie
• Choc cardiogénique
• Insuffisance cardiaque décompensée

**Surveillance:**
• PA continue
• ECG, FC
• Position déclive (hypotension orthostatique)`
    },
    {
      id: 'mannitol',
      commercial: 'MANNITOL 20%®',
      dci: 'Mannitol',
      indication: 'HTIC, TC grave',
      preparation: 'Flacons 250 mL',
      posologie: `**Traumatisme crânien grave:**
• **Dose:** 0,5-1 g/kg
• **= 2,5-5 mL/kg de Mannitol 20%**
• **= 250 mL pour adulte 70 kg**
• **Perfusion:** 15-20 minutes

**Indications:**
• GCS ≤ 8 avec signes HTIC:
  - Mydriase/anisocorie
  - Décérébration
  - Doppler transcrânien anormal

**Précautions:**
• **Compensation diurèse osmotique** par NaCl 0,9%
• Monitoring natrémie (< 155 mmol/L)
• Éviter hypovolémie

**⚠️ Contre-indications:**
• Hémorragie intracrânienne évolutive
• OAP
• Anurie

**Alternative:**
• SSH 3% si mannitol indisponible`
    },
    {
      id: 'metoclopramide',
      commercial: 'PRIMPÉRAN®',
      dci: 'Métoclopramide',
      indication: 'Nausées, vomissements',
      preparation: 'Ampoules 10 mg/2 mL',
      posologie: `**Antiémétique:**
• **Dose:** 10 mg IV lente
• **Répétition:** Toutes les 6-8h
• **Maximum:** 30 mg/24h

**⚠️ Effets indésirables:**
• Syndrome extrapyramidal
• Somnolence
• Dyskinésies

**Contre-indications:**
• Hémorragie digestive
• Occlusion intestinale
• Épilepsie
• Phéochromocytome
• Parkinson

**Précautions:**
• ↓ dose sujet âgé (5 mg)
• Éviter usage prolongé
• Prudence enfant < 18 ans

**Alternative:**
• Ondansétron (Zophren®) 4-8 mg IV`
    },
    {
      id: 'methylprednisolone',
      commercial: 'SOLUMÉDROL®',
      dci: 'Méthylprednisolone',
      indication: 'Asthme, BPCO, œdème laryngé, allergie',
      preparation: 'Flacons 40 mg, 120 mg',
      posologie: `**Asthme aigu grave:**
• **Dose:** 1-2 mg/kg (max 80 mg)
• **Voie:** IV ou PO
• **Répétition:** Toutes les 6h si besoin

**Exacerbation BPCO:**
• **Dose:** 0,5 mg/kg (max 40 mg)

**Œdème laryngé:**
• **Dose:** 120 mg IV
• **Répétition:** Toutes les 6h

**Choc anaphylactique (adjuvant):**
• **Dose:** 1-2 mg/kg

**⚠️ Délai d'action:**
• 4-6 heures
• Pas d'effet immédiat

**Équivalence:**
• 4 mg méthylprednisolone = 5 mg prednisolone
• = 20 mg hydrocortisone

**Effets indésirables:**
• Hyperglycémie
• Immunosuppression
• Troubles psychiatriques`
    },
    {
      id: 'midazolam',
      commercial: 'HYPNOVEL®',
      dci: 'Midazolam',
      indication: 'Sédation, anxiolyse, état de mal',
      preparation: 'Ampoules 5 mg/5 mL, 5 mg/1 mL',
      posologie: `**Sédation procédurale:**
• **Titration:** 0,5-1 mg/2 min
• **Dose totale:** 2-5 mg
• **Début action:** 2-3 min
• **Durée:** 15-30 min

**Induction (co-induction):**
• **Dose:** 0,1-0,3 mg/kg

**État de mal épileptique:**
• **Bolus:** 0,15 mg/kg IVL
• **IVSE:** 0,05-0,4 mg/kg/h

**Sédation IVSE (réanimation):**
• **Dose:** 0,03-0,2 mg/kg/h

**⚠️ Effets indésirables:**
• Dépression respiratoire
• Hypotension (doses rapides)
• Amnésie antérograde
• Paradoxal agitation (sujet âgé)

**Antidote:**
• Flumazénil (Anexate®) 0,25 mg IVL`
    },
    {
      id: 'morphine',
      commercial: 'MORPHINE CHLORHYDRATE®',
      dci: 'Morphine',
      indication: 'Douleur intense, OAP',
      preparation: 'Ampoules 10 mg/1 mL',
      posologie: `**Titration analgésique:**
• **Dilution:** 1 ampoule 10 mg dans 9 mL = 1 mg/mL
• **Bolus:** 2-3 mg (0,05 mg/kg) IVL
• **Répétition:** Toutes les 5 min
• **Évaluation:** EVA après chaque bolus
• **Objectif:** EVA < 3

**OAP cardiogénique:**
• **Dose:** 3-5 mg IVL
• **Répétition:** 2-3 mg si besoin
• ⚠️ Prudence si hypotension

**Perfusion continue:**
• **Dose:** 2-10 mg/h après titration

**⚠️ Effets indésirables:**
• Dépression respiratoire
• Nausées/vomissements
• Hypotension
• Prurit
• Constipation
• Rétention urinaire

**Antidote:**
• Naloxone 0,04-0,4 mg IVL

**Surveillance:**
• FR, SpO2, PA
• Sédation (échelle Ramsay)`
    },
    {
      id: 'naloxone',
      commercial: 'NARCAN®',
      dci: 'Naloxone',
      indication: 'Antagoniste morphiniques',
      posologie: `**Surdosage opiacés:**
• **Titration:** 0,04 mg IVL (1/10 ampoule)
• **Répétition:** Toutes les 2-3 min
• **Maximum initial:** 0,4 mg
• **Objectif:** FR > 10/min, réponse stimuli

**Intoxication sévère:**
• **Bolus:** 0,4-2 mg
• **IVSE:** 0,4-0,8 mg/h (durée action courte)

**Nouveau-né (mère sous opiacés):**
• **Dose:** 0,01 mg/kg IM/IV/SC

**⚠️ Précautions:**
• Demi-vie courte (30-90 min)
• Risque re-sédation
• Syndrome sevrage brutal
• OAP flash (rare)

**Surveillance:**
• FR, conscience
• Répéter doses si re-sédation`
    },
    {
      id: 'nicardipine',
      commercial: 'LOXEN®',
      dci: 'Nicardipine',
      indication: 'HTA sévère, angor instable',
      preparation: 'Ampoules 10 mg/10 mL',
      posologie: `**Urgence hypertensive:**
• **Perfusion IVSE:** 3-15 mg/h
• **Démarrage:** 1-3 mg/h
• **Titration:** ↑ 1 mg/h/15 min
• **Objectif:** ↓ PA 20-25% en 1-2h

**Préparation:**
• **4 ampoules (40 mg) dans 200 mL G5%**
• **= 0,2 mg/mL**
• **Débit (mL/h) = Dose (mg/h) × 5**

**Dissection aortique:**
• **Objectif:** PAS 100-120 mmHg
• **Association:** Bêta-bloquant

**⚠️ Effets indésirables:**
• Hypotension
• Tachycardie réflexe
• Céphalées
• Flush

**Surveillance:**
• PA continue (toutes les 5 min)
• ECG
• Débit urinaire`
    },
    {
      id: 'noradrenaline',
      commercial: 'NORADRÉNALINE AGUETTANT®',
      dci: 'Noradrénaline',
      indication: 'Choc septique, choc hémorragique',
      preparation: 'Ampoules 8 mg/4 mL',
      posologie: `**Choc (toutes causes):**
• **Objectif:** PAM ≥ 65 mmHg (≥ 80 si TC)
• **Dose initiale:** 0,05-0,1 µg/kg/min
• **Titration:** ↑ 0,05-0,1 µg/kg/min/5-15 min
• **Dose usuelle:** 0,1-0,5 µg/kg/min
• **Maximum:** 2-3 µg/kg/min

**Préparation standard:**
• **8 mg dans 40 mL (NaCl/G5%):** 0,2 mg/mL
• **Débit (mL/h) = Dose (µg/kg/min) × Poids × 0,3**

**Voie administration:**
• **VVC préférable**
• **VVP possible** si urgence (dédiée, G20+)

**⚠️ Effets indésirables:**
• Ischémie tissulaire périphérique
• Nécrose (extravasation)
• Arythmies
• Hyperglycémie

**Surveillance:**
• PA invasive si possible
• Débit urinaire
• Lactates
• Marbrures`
    },
    {
      id: 'omeprazole',
      commercial: 'MOPRAL®',
      dci: 'Oméprazole',
      indication: 'Hémorragie digestive haute, ulcère',
      preparation: 'Flacons 40 mg',
      posologie: `**Hémorragie digestive haute:**
• **Bolus:** 80 mg IVL (dilué 100 mL)
• **Perfusion:** 8 mg/h continu
• **Durée:** 72h
• **Préparation:** 4 flacons (160 mg) dans 200 mL

**Ulcère gastroduodénal:**
• **Dose:** 40 mg/j IV

**Prévention ulcère stress:**
• **Dose:** 40 mg/j

**⚠️ Interactions:**
• Clopidogrel (↓ efficacité)
• Anticoagulants
• Antirétroviraux

**Effets indésirables:**
• Céphalées
• Diarrhée
• Hypomagnésémie (long terme)
• Fractures (long terme)`
    },
    {
      id: 'ondansetron',
      commercial: 'ZOPHREN®',
      dci: 'Ondansétron',
      indication: 'Nausées, vomissements',
      preparation: 'Ampoules 4 mg/2 mL, 8 mg/4 mL',
      posologie: `**Nausées/vomissements:**
• **Dose:** 4-8 mg IVL
• **Répétition:** Toutes les 8h
• **Maximum:** 32 mg/24h

**Prévention NVPO:**
• **Dose:** 4 mg en fin d'intervention

**Chimiothérapie:**
• **Dose:** 8-16 mg avant chimio

**⚠️ Effets indésirables:**
• Allongement QT (doses élevées)
• Céphalées
• Constipation

**Contre-indications:**
• QT long congénital
• Association apomorphine

**Avantages vs Métoclopramide:**
• Pas de syndrome extrapyramidal
• Plus efficace`
    },
    {
      id: 'paracetamol',
      commercial: 'PERFALGAN®',
      dci: 'Paracétamol',
      indication: 'Douleur, fièvre',
      preparation: 'Flacons 1 g/100 mL',
      posologie: `**Adulte > 50 kg:**
• **Dose:** 1 g
• **Voie:** IV lente (15 min minimum)
• **Intervalle:** 6h minimum
• **Maximum:** 4 g/24h

**Adulte < 50 kg:**
• **Dose:** 15 mg/kg/6h
• **Maximum:** 60 mg/kg/24h

**Enfant:**
• **Dose:** 15 mg/kg/6h
• **Maximum:** 60 mg/kg/24h

**⚠️ Contre-indications:**
• Insuffisance hépatocellulaire
• Hypersensibilité

**Précautions:**
• ↓ dose si insuffisance rénale
• Dénutrition, alcoolisme
• Déshydratation

**Toxicité:**
• Surdosage > 150 mg/kg
• Hépatotoxicité
• Antidote: N-acétylcystéine`
    },
    {
      id: 'propofol',
      commercial: 'DIPRIVAN®',
      dci: 'Propofol',
      indication: 'Induction, sédation',
      preparation: 'Ampoules 10 mg/mL (200 mg/20 mL)',
      posologie: `**Induction (ISR):**
• **Dose:** 1,5-2,5 mg/kg IVL
• **Sujet âgé/ASA 3-4:** 1-1,5 mg/kg
• **Début action:** 30-45 sec
• **Durée:** 5-10 min

**Sédation IVSE:**
• **Dose:** 1-4 mg/kg/h
• **Titration:** 0,5 mg/kg/h/10 min

**Sédation procédurale:**
• **Bolus:** 0,5 mg/kg
• **Répétition:** 10-20 mg/2-3 min

**⚠️ Effets indésirables:**
• Hypotension dose-dépendante
• Dépression respiratoire/apnée
• Douleur injection (lidocaïne préventive)
• Hypertriglycéridémie (perfusion prolongée)

**Contre-indications:**
• Allergie œuf, soja
• Enfant < 3 ans (sédation)

**Avantages:**
• Réveil rapide
• Antiémétique`
    },
    {
      id: 'salbutamol',
      commercial: 'VENTOLINE®',
      dci: 'Salbutamol',
      indication: 'Bronchospasme, asthme, BPCO',
      preparation: 'Nébulisation 5 mg/2,5 mL, aérosol-doseur',
      posologie: `**Asthme aigu grave:**
• **Nébulisation:** 5 mg (1 unidose)
• **En continu sur 1h** sous O2
• **Répétition:** Selon réponse clinique

**Exacerbation BPCO:**
• **Nébulisation:** 5 mg
• **Répétition:** Toutes les 20 min si besoin

**Aérosol-doseur (asthme modéré):**
• **Dose:** 4-8 bouffées/chambre
• **Répétition:** Toutes les 20 min

**Hyperkaliémie (off-label):**
• **Nébulisation:** 10-20 mg

**⚠️ Effets indésirables:**
• Tachycardie, palpitations
• Tremblements
• Hypokaliémie
• Hyperglycémie

**Association:**
• Ipratropium (Atrovent®) 0,5 mg`
    },
    {
      id: 'suxamethonium',
      commercial: 'CÉLOCURINE®',
      dci: 'Suxaméthonium (Succinylcholine)',
      indication: 'Curarisation rapide (ISR)',
      preparation: 'Ampoules 100 mg/2 mL',
      posologie: `**Intubation séquence rapide:**
• **Dose:** 1-1,5 mg/kg IVD
• **Début action:** 30-60 sec
• **Durée:** 5-10 min

**Précurarisation (optionnelle):**
• **Rocuronium 0,06 mg/kg 3 min avant**
• Réduit fasciculations

**⚠️ Contre-indications:**
• Hyperkaliémie
• Brûlures étendues (> 24h)
• Crush syndrome (> 24h)
• Myopathies
• Antécédent hyperthermie maligne
• Allergie

**Effets indésirables:**
• Fasciculations
• Hyperkaliémie (+0,5-1 mmol/L)
• Myalgies post-opératoires
• Hyperthermie maligne (rare)
• Bradycardie (2ème dose)

**Avantages:**
• Début action ultra-rapide
• Durée courte
• Pas d'antagoniste nécessaire`
    },
    {
      id: 'tramadol',
      commercial: 'CONTRAMAL®',
      dci: 'Tramadol',
      indication: 'Douleur modérée à intense',
      preparation: 'Ampoules 100 mg/2 mL',
      posologie: `**Douleur modérée:**
• **Dose:** 50-100 mg IV lente
• **Répétition:** Toutes les 4-6h
• **Maximum:** 400 mg/24h

**Titration:**
• **Bolus:** 50 mg/10 min
• **Évaluation EVA**

**⚠️ Effets indésirables:**
• Nausées, vomissements (30%)
• Vertiges
• Somnolence
• Convulsions (doses élevées)
• Syndrome sérotoninergique (association ISRS)

**Contre-indications:**
• Épilepsie non contrôlée
• Insuffisance respiratoire sévère
• Intoxication aiguë alcool/psychotropes

**Avantages:**
• Moins de dépression respiratoire
• Pas de stupéfiant (pas de registre)

**Inconvénients:**
• Moins puissant que morphine
• Nausées fréquentes`
    }
  ];

  // Filter medications based on search term
  const filteredMedications = medications.filter(med => {
    const searchLower = searchTerm.toLowerCase();
    return (
      med.commercial.toLowerCase().includes(searchLower) ||
      med.dci.toLowerCase().includes(searchLower) ||
      med.indication.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl" data-testid="medicaments-adulte-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl border border-cyan-400/30">
            <Pill className="w-8 h-8 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--text))]">Médicaments Adulte</h1>
            <p className="text-[hsl(var(--text-dim))]">Guide des médicaments d'urgence - SMUR 2025</p>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-dim))]" />
          <Input
            type="text"
            placeholder="Rechercher un médicament par nom, DCI ou indication..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 text-base bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]"
            data-testid="medication-search-input"
          />
        </div>

        {/* Results Count */}
        {searchTerm && (
          <p className="mt-3 text-sm text-[hsl(var(--text-dim))]">
            {filteredMedications.length} médicament{filteredMedications.length > 1 ? 's' : ''} trouvé{filteredMedications.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Quick Access Links to Protocols */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-[hsl(var(--text))] mb-3">Accès rapide aux protocoles</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Link 
              to="/protocols/69365c2f98c6705b68ab3758" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 hover:from-red-500/20 hover:to-red-600/20 border border-red-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Heart className="w-6 h-6 text-red-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Arrêt cardiaque</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab375b" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-orange-500/10 to-orange-600/10 hover:from-orange-500/20 hover:to-orange-600/20 border border-orange-400/30 rounded-lg transition-all hover:scale-105"
            >
              <ShieldAlert className="w-6 h-6 text-orange-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Anaphylaxie</span>
            </Link>

            <Link 
              to="/protocols" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20 border border-blue-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Activity className="w-6 h-6 text-blue-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Douleur</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab375a" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 hover:from-yellow-500/20 hover:to-yellow-600/20 border border-yellow-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Brain className="w-6 h-6 text-yellow-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Épilepsie</span>
            </Link>

            <Link 
              to="/protocols" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/10 hover:from-purple-500/20 hover:to-purple-600/20 border border-purple-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Droplet className="w-6 h-6 text-purple-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Choc</span>
            </Link>

            <Link 
              to="/protocols/69365c2f98c6705b68ab3759" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-violet-500/10 to-violet-600/10 hover:from-violet-500/20 hover:to-violet-600/20 border border-violet-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Zap className="w-6 h-6 text-violet-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">AVC</span>
            </Link>

            <Link 
              to="/antibio" 
              className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-green-500/10 to-green-600/10 hover:from-green-500/20 hover:to-green-600/20 border border-green-400/30 rounded-lg transition-all hover:scale-105"
            >
              <Bug className="w-6 h-6 text-green-400" />
              <span className="text-sm font-medium text-center text-[hsl(var(--text))]">Infection</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 text-[hsl(var(--text))]">
            <Pill className="w-5 h-5 text-cyan-400" />
            Liste des Médicaments
          </CardTitle>
          <CardDescription>
            Cliquez sur un médicament pour afficher les informations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            {filteredMedications.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
                <p className="text-[hsl(var(--text-dim))]">Aucun médicament trouvé</p>
                <p className="text-sm text-[hsl(var(--text-dim))] mt-2">
                  Essayez un autre terme de recherche
                </p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full space-y-4">
                {filteredMedications.map((medication) => {
                  const isLoading = aiLoading[medication.id];
                  const aiResult = aiResults[medication.id];

                  return (
                    <AccordionItem
                      key={medication.id}
                      value={medication.id}
                      className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                      data-testid={`medication-item-${medication.id}`}
                    >
                      <AccordionTrigger
                        className="px-6 py-4 hover:no-underline hover:bg-[hsl(var(--bg-elev))] transition-colors"
                        data-testid={`accordion-trigger-${medication.id}`}
                      >
                        <div className="flex flex-col items-start gap-1 w-full text-left">
                          <div className="flex items-center gap-2">
                            <Pill className="w-4 h-4 text-cyan-400" />
                            <span className="font-semibold text-[hsl(var(--text))]">
                              {medication.commercial}
                            </span>
                          </div>
                          <span className="text-sm text-[hsl(var(--text-dim))]">
                            {medication.dci}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 py-4 space-y-4">
                        <div className="space-y-4">
                          {/* Medication Details */}
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-sm font-semibold text-cyan-400 mb-1">DCI:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.dci}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-cyan-400 mb-1">Indication:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.indication}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-cyan-400 mb-1">Préparation:</h4>
                              <p className="text-[hsl(var(--text))]">{medication.preparation}</p>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-semibold text-cyan-400 mb-1">Posologie:</h4>
                              <div className="prose prose-invert max-w-none">
                                <div className="text-sm text-[hsl(var(--text))] whitespace-pre-wrap leading-relaxed">
                                  {medication.posologie.split('\n').map((line, idx) => {
                                    if (line.startsWith('**') && line.endsWith('**')) {
                                      return (
                                        <h5 key={idx} className="text-base font-semibold text-cyan-400 mt-3 mb-1">
                                          {line.replace(/\*\*/g, '')}
                                        </h5>
                                      );
                                    }
                                    if (line.startsWith('• ')) {
                                      return (
                                        <div key={idx} className="flex items-start gap-2 my-1">
                                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                                          <span>{line.substring(2)}</span>
                                        </div>
                                      );
                                    }
                                    if (line.startsWith('⚠️')) {
                                      return (
                                        <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                          <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                                          <span className="text-orange-300">{line.substring(2)}</span>
                                        </div>
                                      );
                                    }
                                    return line.trim() ? <p key={idx} className="my-1">{line}</p> : <div key={idx} className="h-1" />;
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* AI Analysis Button */}
                          <Button
                            onClick={() => handleAiAnalysis(
                              medication.id, 
                              medication.commercial,
                              `${medication.dci}\n\nIndication: ${medication.indication}\n\nPosologie: ${medication.posologie}`
                            )}
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                            data-testid={`ai-button-${medication.id}`}
                          >
                            {isLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyse AI en cours...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-2" />
                                Analyse AI de ce médicament
                              </>
                            )}
                          </Button>

                          {/* AI Result */}
                          {aiResult && (
                            <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                              <h4 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                                <Sparkles className="w-5 h-5" />
                                Analyse AI
                              </h4>
                              <div className="prose prose-invert max-w-none">
                                <div 
                                  className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" 
                                  data-testid={`ai-result-${medication.id}`}
                                >
                                  {aiResult}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
